import React from 'react';
import {useLocation} from 'react-router-dom';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {BaselineEditPageQuery} from './BaselineEditPageQuery';
import BaselineEditPage from './BaselineEditPage';
import {VERSION_PARAM} from './versions/useSelectedBaselineVersion';
import {FEATURE_FLAGS} from '../../constants';
import type {BaselineEditPageQuery$data} from './__generated__/BaselineEditPageQuery.graphql';

/**
 * Loads the Baseline edit page. A `?version=<id>` in the URL loads that Baseline version's rows for editing. The API
 * answers an id it cannot serve (a typo, another project's version, a deleted version, or a viewer who cannot read
 * versions) with the live rows, and Standard's id with the live rows too, as Standard is the live Baseline. So the edit
 * page is handed the id only when it is a version other than Standard in the project's version list from the same
 * response, active or not, and saves to the Baseline it loaded. While the versions flag is off the live Baseline loads,
 * with no version id in the query at all. Leaving the edit page returns to the version the URL named whenever it is in
 * the list, Standard included, so editing Standard returns to Standard's tab even while another version is active.
 */
export const BaselineEditQueryRoute = ({match}: {match: {params: Record<string, string>}}): JSX.Element => {
	const {search} = useLocation();
	const urlVersionId = hasFeatureFlag(FEATURE_FLAGS.BASELINE_VERSIONS)
		? new URLSearchParams(search).get(VERSION_PARAM)
		: null;
	return (
		<ForecastQueryRenderer
			key="query-render-baseline-edit"
			query={BaselineEditPageQuery}
			variables={{
				projectId: match.params.projectId,
				hasDeliverablesFeature: hasFeatureFlag('deliverables'),
				...(urlVersionId ? {baselineVersionId: urlVersionId} : {}),
			}}
			authorizeAccessRoute={'baseline'}
			render={relayProps => {
				const {viewer} = relayProps as BaselineEditPageQuery$data;
				const versions = viewer?.project?.baselineVersions ?? [];
				const listed = versions.find(v => v?.id === urlVersionId);
				const loadedVersionId = listed && !listed.isStandard ? listed.id : undefined;
				return (
					<BaselineEditPage
						{...relayProps}
						baselineVersionId={loadedVersionId}
						returnVersionId={listed ? listed.id : undefined}
					/>
				);
			}}
		/>
	);
};
